<template>
  <div class="plate plate--wm">
    <div class="plate__media">
      <div v-if="logos[integration.attributes.service]" class="plate__logo">
        <img :src="logos[integration.attributes.service]" :alt="integration.attributes.service" />
      </div>
      <div class="plate__title">
        {{ integration.attributes.service }} {{ getSandboxPrefix(integration) }}
      </div>
    </div>

    <div class="plate__data">
      <div class="plate__date">{{ getDateFormat(integration.attributes.created_at) }}</div>

      <div class="plate__status" :class="{ 'text-success': isEnabled, 'text-danger': !isEnabled }">
        <b-form-checkbox
          v-model="isEnabled"
          switch
          @change="toggleIntegration"
          :disabled="isToggling"
        >
          {{
            isEnabled
              ? $t("integration.enabled", "Enabled")
              : $t("integration.disabled", "Disabled")
          }}
        </b-form-checkbox>
      </div>

      <div
        v-if="showCampaignWarning"
        class="plate__warning"
        v-b-tooltip.hover
        :title="
          $t(
            'integration.no_campaign_warning',
            'No campaign ID assigned. This integration will not export data until at least one campaign ID is added.'
          )
        "
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="#e6a23c"
        >
          <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
        </svg>
      </div>

      <button
        v-if="showSyncButton"
        class="bttn bttn--sm bttn--orange plate__sync"
        :disabled="!hasEditAccess"
        @click.prevent="openSyncModal"
      >
        {{ $t("integration.sync_now", "Sync now") }}
      </button>

      <div class="plate__action" @click.prevent="gatewayOverview">
        <IconOverview />
      </div>
    </div>

    <div v-if="showHealth" class="plate__health">
      <b-badge :variant="health.auto_disabled ? 'danger' : 'warning'">
        {{ healthBadgeText }}
      </b-badge>

      <div v-if="health.auto_disabled" class="plate__health-hint">
        {{
          $t(
            "integration.health_enable_hint",
            "Fix the cause, then switch it back on — this clears the error count."
          )
        }}
      </div>

      <div v-if="health.last_error" class="plate__health-error">
        <span class="plate__health-error-label">
          {{ $t("integration.health_last_error", "Last error") }}
        </span>
        <span v-if="health.last_error_at" class="plate__health-error-date">
          {{ getIsoDateFormat(health.last_error_at) }}
        </span>
        <div class="plate__health-error-text" v-b-tooltip.hover :title="health.last_error">
          {{ health.last_error }}
        </div>
      </div>
    </div>

    <integration-sync
      v-if="showSyncButton"
      :integration="integration"
      :modal-id="syncModalId"
      @enable-integration="enableFromSync"
    />
  </div>
</template>

<script>
import { mapState } from "vuex";
import axios from "axios";
import urls from "@/constants/urls.js";
import moment from "moment";
import IconOverview from "./icon-overview.vue";
import integrationSync from "./integration-sync.vue";

export default {
  components: {
    IconOverview,
    integrationSync,
  },
  props: {
    integration: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      isEnabled: this.integration.attributes.status === 1,
      isToggling: false,
    };
  },

  methods: {
    gatewayOverview() {
      this.$emit("view-data", this.integration);
    },

    getDateFormat: function (date) {
      return moment.unix(date).format("MMMM D, YYYY h:mm a");
    },

    // `health.last_error_at` is an RFC3339 string, unlike `created_at` above.
    getIsoDateFormat: function (date) {
      return moment(date).format("MMMM D, YYYY h:mm a");
    },

    openSyncModal() {
      this.$bvModal.show(this.syncModalId);
    },

    enableFromSync() {
      if (this.isEnabled || this.isToggling) {
        return;
      }
      this.isEnabled = true;
      this.toggleIntegration();
    },

    getSandboxPrefix(s) {
      switch (s.attributes.service) {
        case "admire":
        case "donorfuse":
          if (s.attributes.sandbox) {
            return "(Sandbox)";
          }
      }
      return "";
    },

    async toggleIntegration() {
      this.isToggling = true;
      try {
        await axios.patch(urls.toggleIntegration(this.orgId, this.integration.id), {
          enable: this.isEnabled,
        });
        this.$_notificationsMixin_makeToast(
          "Success",
          this.isEnabled
            ? this.$t("integration.enabled_success", "Integration enabled successfully")
            : this.$t("integration.disabled_success", "Integration disabled successfully")
        );
        this.$store.dispatch("getIntegrationsList");
      } catch (error) {
        this.$_notificationsMixin_handleCatch(error);
        this.isEnabled = !this.isEnabled; // Revert the toggle if request fails
      } finally {
        this.isToggling = false;
      }
    },
  },

  computed: {
    // Absent on older backends - every read below has to tolerate that.
    health() {
      return this.integration.attributes.health || null;
    },

    showHealth() {
      return Boolean(this.health && (this.health.auto_disabled || this.health.error_count > 0));
    },

    healthBadgeText() {
      const count = this.health?.error_count || 0;
      const threshold = this.health?.error_threshold || 0;

      if (this.health?.auto_disabled) {
        return this.$t(
          "integration.health_auto_disabled",
          "Disabled automatically after {count} failed runs",
          { count }
        );
      }

      // `error_threshold` is 0 when the backend could not read it - do not
      // render a "12 of 0" ratio in that case.
      if (!threshold) {
        return this.$t("integration.health_failing_no_threshold", "{count} failed runs", {
          count,
        });
      }

      return this.$t("integration.health_failing", "{count} of {threshold} failed runs", {
        count,
        threshold,
      });
    },

    syncModalId() {
      return `integration-sync-${this.integration.id}`;
    },

    showSyncButton() {
      return this.integration.attributes.service === "salesforce";
    },

    requiresCampaignIds() {
      switch (this.integration.attributes.service) {
        case "admire":
        case "donorfuse":
        case "jl":
          return true;
      }
      return false;
    },

    campaignIds() {
      if (!this.requiresCampaignIds) return [];
      try {
        const meta2 = JSON.parse(this.integration.attributes.meta_2);
        return meta2.autoexport_for_campaigns || [];
      } catch {
        return [];
      }
    },

    showCampaignWarning() {
      return this.isEnabled && this.requiresCampaignIds && this.campaignIds.length === 0;
    },

    ...mapState({
      hasEditAccess: (state) => state.account.access.CanSeeAndEditOrganizationIntegrationList,
      orgId: () => localStorage.getItem("orgId"),
      logos: function () {
        let logos = {
          admire:
            "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/public_upload/1754411833_3434623265366439353062326236623162366338313436336330336239346664_31373534343131383333.png",
          salesforce:
            "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/charidy_images/services/salesforce-logo.png",
          mailchimp:
            "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/charidy_images/services/mailchimp-logo.png",
          overpass:
            "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/charidy_images/services/overpass.svg",
          zapier:
            "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/images/281/zapier_logo_1657439051.png",
          tax_gift:
            "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/images/7943/taxgift_1660867465.png",
          comeunity:
            "https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/images/281/file_1662029944.png",
        };
        return logos;
      },
    }),
  },
};
</script>

<style lang="scss" scoped>
// The health block sits on its own line under the plate row, so the plate
// has to be allowed to wrap on the desktop breakpoint where it is a row.
.plate {
  flex-wrap: wrap;
}

.plate__sync {
  margin-right: 15px;
  white-space: nowrap;
}

.plate__health {
  flex: 0 0 100%;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #e6eaed;
  font-size: 14px;

  &-hint {
    margin-top: 5px;
    color: #6c757d;
  }

  &-error {
    margin-top: 5px;

    &-label {
      font-weight: 600;
    }

    &-date {
      margin-left: 5px;
      color: #6c757d;
    }

    &-text {
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      word-break: break-word;
      cursor: help;
    }
  }
}

.gateway-plate {
  padding: 20px;
  background-color: #ffffff;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  @media only screen and (min-width: 992px) {
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
  }
  &:not(:last-of-type) {
    margin-bottom: 20px;
  }
  &__media {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    min-width: 180px;
    margin-right: 20px;
    margin-bottom: 20px;
    @media only screen and (min-width: 992px) {
      margin-bottom: initial;
    }
  }
  &__logo {
    display: flex;
    align-items: center;
    height: 20px;
    min-width: 80px;
    max-width: 80px;
    overflow: hidden;
    img {
      height: 20px;
      width: auto;
    }
  }
  &__title {
    font-size: 16px;
    padding: 0 0 0 10px;
    text-transform: capitalize;
  }
  &__owner {
    padding-right: 10px;
    font-size: 16px;
    color: #20434e;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-bottom: 20px;
    @media only screen and (min-width: 992px) {
      margin-right: 20px;
      margin-bottom: initial;
    }
  }
  &__data {
    margin-left: auto;
    display: flex;
    align-items: center;
    width: 100%;
    justify-content: space-between;
    @media only screen and(min-width: 992px) {
      width: auto;
      justify-content: initial;
    }
  }
  &__ccy {
    margin-right: 15px;
    font-size: 16px;
    font-weight: 600;
    text-transform: uppercase;
  }

  &__verification {
    margin-right: 15px;
    min-width: 90px;
    font-size: 16px;
    text-transform: capitalize;
  }
  &__status {
    margin-right: 15px;
    min-width: 65px;
    font-size: 16px;
    color: gray;
    text-transform: capitalize;
    display: flex;
    align-items: center;
    .custom-switch {
      margin-right: 8px;
    }
  }
  &__warning {
    display: inline-flex;
    align-items: center;
    margin-right: 10px;
    cursor: help;
  }
  &__action {
    min-height: 25px;
    min-width: 25px;
    display: inline-flex;
    align-items: center;
    cursor: pointer;
    svg {
      width: 22px;
      height: auto;
      fill: #cc6d4c;
    }
  }
}
</style>
